/**
 * Hero Carousel Component
 *
 * Displays rotating promotional banners from admin-configured slides.
 * Falls back to default hero if no slides are configured.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useHeroSettings } from '../../contexts/SettingsContext';
import type { HeroSlide } from '@petmas/shared';
import { CART_CONSTANTS } from '@petmas/shared';

export function HeroCarousel() {
  const { settings, slides } = useHeroSettings();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const hasSlides = slides.length > 0;
  const totalSlides = slides.length;

  // Auto-advance slides
  useEffect(() => {
    if (!hasSlides || !settings.autoplayEnabled || isPaused) return;

    const timer = globalThis.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, settings.autoplayInterval);

    return () => globalThis.clearInterval(timer);
  }, [hasSlides, settings.autoplayEnabled, settings.autoplayInterval, isPaused, totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  // If no slides, show default hero
  if (!hasSlides) {
    return <DefaultHero />;
  }

  return (
    <section
      className="relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Slides Container */}
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * CART_CONSTANTS.PROGRESS_PERCENTAGE_FULL}%)` }}
      >
        {slides.map((slide) => (
          <SlideItem key={slide.id} slide={slide} />
        ))}
      </div>

      {/* Navigation Arrows */}
      {settings.showArrows && totalSlides > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
            aria-label="Anterior"
          >
            <ChevronLeftIcon className="w-6 h-6 text-gray-800" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
            aria-label="Siguiente"
          >
            <ChevronRightIcon className="w-6 h-6 text-gray-800" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {settings.showIndicators && totalSlides > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Ir a slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SlideItem({ slide }: { slide: HeroSlide }) {
  const backgroundStyle = slide.backgroundColor
    ? { backgroundColor: slide.backgroundColor }
    : undefined;

  const content = (
    <div
      className="relative w-full min-h-[300px] md:min-h-[400px] lg:min-h-[500px] flex-shrink-0"
      style={backgroundStyle}
    >
      {/* Background Image */}
      <picture>
        {slide.imageUrlMobile && (
          <source
            media="(max-width: 768px)"
            srcSet={slide.imageUrlMobile}
          />
        )}
        <img
          src={slide.imageUrl}
          alt={slide.title ?? 'Promotional banner'}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </picture>

      {/* Overlay for text readability */}
      {(slide.title || slide.subtitle) && (
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
      )}

      {/* Content */}
      {(slide.title || slide.subtitle || slide.linkUrl) && (
        <div className="relative container h-full flex items-center py-12 md:py-20">
          <div className="max-w-xl" style={{ color: slide.textColor }}>
            {slide.title && (
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                {slide.title}
              </h2>
            )}
            {slide.subtitle && (
              <p className="mt-4 text-lg md:text-xl opacity-90">
                {slide.subtitle}
              </p>
            )}
            {slide.linkUrl && slide.linkText && (
              <div className="mt-8">
                <SlideLink url={slide.linkUrl} text={slide.linkText} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // If the entire slide is a link (no specific link text)
  if (slide.linkUrl && !slide.linkText) {
    if (slide.linkUrl.startsWith('/')) {
      return <Link to={slide.linkUrl} className="block">{content}</Link>;
    }
    return <a href={slide.linkUrl} className="block" target="_blank" rel="noopener noreferrer">{content}</a>;
  }

  return content;
}

function SlideLink({ url, text }: { url: string; text: string }) {
  const className = "btn bg-white text-gray-900 hover:bg-gray-100 font-semibold";

  if (url.startsWith('/')) {
    return <Link to={url} className={className}>{text}</Link>;
  }
  return <a href={url} className={className} target="_blank" rel="noopener noreferrer">{text}</a>;
}

// Default hero when no slides are configured
function DefaultHero() {
  return (
    <section className="relative bg-cream min-h-[400px] md:min-h-[500px] overflow-hidden">
      {/* Paw print pattern background */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath fill='%235d4e37' d='M30 20c-2 0-4 2-4 5s2 5 4 5 4-2 4-5-2-5-4-5zm-10 5c-1.5 0-3 1.5-3 3.5s1.5 3.5 3 3.5 3-1.5 3-3.5-1.5-3.5-3-3.5zm20 0c-1.5 0-3 1.5-3 3.5s1.5 3.5 3 3.5 3-1.5 3-3.5-1.5-3.5-3-3.5zm-15 10c-1.5 0-3 1.5-3 3.5s1.5 3.5 3 3.5 3-1.5 3-3.5-1.5-3.5-3-3.5zm10 0c-1.5 0-3 1.5-3 3.5s1.5 3.5 3 3.5 3-1.5 3-3.5-1.5-3.5-3-3.5z'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px'
      }} />

      <div className="container relative z-10 py-16 md:py-24 flex items-center justify-center">
        <div className="text-center max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-display font-bold text-secondary leading-tight">
            LOS MEJORES PRODUCTOS
          </h1>
          <p className="mt-4 text-2xl md:text-4xl font-display font-bold text-secondary">
            PARA TU COMPAÑERO DE 4 PATAS
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/perros" className="btn btn-primary">
              Comprar para Perros
            </Link>
            <Link to="/gatos" className="btn btn-outline">
              Comprar para Gatos
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Icons
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
